/** 来源：market ToastProvider/useToast + CRM ToastStack；统一 aria-live、自动消失与手动关闭。 */
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { alertToneClasses, type Tone } from './tones'
import { overlayPanelTransition, useEnterTransition } from './use-overlay'
import { FailureInfo } from './Form'
import type { describeFailure } from './error-feedback'

export interface ToastItem {
  id: number
  text: string
  tone: Tone
  duration?: number
  failure?: ReturnType<typeof describeFailure>
}

export interface ToastNotice {
  id: number
  message: string
  tone: 'success' | 'error'
  failure?: ReturnType<typeof describeFailure>
}

export type Toast = (text: string, tone?: Tone, duration?: number, failure?: ReturnType<typeof describeFailure>) => void

const ToastContext = createContext<Toast | null>(null)

function ToastNotice({ item, onDismiss, duration }: { item: ToastItem; onDismiss: (id: number) => void; duration: number }) {
  // 逐条进场过渡：挂载下一拍滑入；motion-safe 门控，reduced-motion 下即时显形
  const entered = useEnterTransition(true)
  const dismissRef = useRef(onDismiss)
  dismissRef.current = onDismiss
  useEffect(() => {
    if (item.tone === 'danger' || duration <= 0) return
    const timer = setTimeout(() => dismissRef.current(item.id), duration)
    return () => clearTimeout(timer)
  }, [item.id, item.tone, duration])
  return (
    <div
      className={`pointer-events-auto flex items-center gap-2 rounded-[var(--radius-md)] border-l-4 px-3.5 py-2.5 text-sm shadow-[var(--shadow-md)] ${alertToneClasses[item.tone]} ${overlayPanelTransition} ${entered ? '' : 'translate-y-2 opacity-0'}`}
      role={item.tone === 'danger' ? 'alert' : 'status'}
      aria-live="polite"
    >
      <div className="min-w-0 flex-1 break-words">{item.text}{item.failure ? <FailureInfo failure={item.failure} /> : null}</div>
      <button
        type="button"
        className="cursor-pointer rounded-[var(--radius-sm)] px-1 text-[var(--color-muted)] transition-colors hover:bg-[var(--color-soft)] focus:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--color-focus)] max-md:inline-flex max-md:min-h-11 max-md:min-w-11 max-md:items-center max-md:justify-center"
        aria-label="关闭通知"
        onClick={() => onDismiss(item.id)}
      >
        ×
      </button>
    </div>
  )
}

type ToastStackProps =
  | { items: ToastItem[]; notices?: never; onDismiss: (id: number) => void; duration?: number; maxItems?: number }
  | { items?: never; notices: ToastNotice[]; onDismiss: (id: number) => void; duration?: number; maxItems?: number }

export function ToastStack({ items, notices, onDismiss, duration, maxItems }: ToastStackProps) {
  const controlledItems: ToastItem[] = items ?? notices.map((notice) => ({
    id: notice.id,
    text: notice.message,
    tone: notice.tone === 'error' ? 'danger' : 'success',
    failure: notice.failure,
  }))
  const unique = new Map<string, ToastItem>()
  for (const item of controlledItems) unique.set(`${item.tone}\0${item.text}`, item)
  const candidates = [...unique.values()]
  const ordinary = candidates.filter((item) => item.tone !== 'danger')
  const ordinarySlots = maxItems ? Math.max(0, maxItems - (candidates.length - ordinary.length)) : ordinary.length
  const visibleOrdinary = ordinarySlots ? ordinary.slice(-ordinarySlots) : []
  const visibleItems = candidates.filter((item) => item.tone === 'danger' || visibleOrdinary.includes(item))
  const dismissRef = useRef(onDismiss)
  dismissRef.current = onDismiss
  const hiddenIds = controlledItems.filter((item) => !visibleItems.includes(item)).map((item) => item.id).join(',')
  useEffect(() => {
    if (hiddenIds) for (const id of hiddenIds.split(',')) dismissRef.current(Number(id))
  }, [hiddenIds])

  return (
    // z-[60]：全包最上层（分层约定见 use-overlay.ts），模态之上的操作反馈也必须可见
    <div className="pointer-events-none fixed right-4 bottom-4 z-[60] flex max-h-[calc(100dvh-2rem)] w-72 max-w-[calc(100vw-2rem)] flex-col gap-2 overflow-y-auto" role="region" aria-label="通知">
      {visibleItems.map((item) => <ToastNotice key={item.id} item={item} onDismiss={onDismiss} duration={item.duration ?? duration ?? 3600} />)}
    </div>
  )
}

export function ToastProvider({ children, duration = 3500 }: { children: ReactNode; duration?: number }) {
  const [items, setItems] = useState<ToastItem[]>([])
  const nextId = useRef(1)

  const dismiss = useCallback((id: number) => {
    setItems((current) => current.filter((item) => item.id !== id))
  }, [])

  const push = useCallback<Toast>((text, tone = 'success', toastDuration, failure) => {
    const id = nextId.current++
    setItems((current) => [...current.filter((item) => item.text !== text || item.tone !== tone), { id, text, tone, duration: toastDuration ?? duration, failure }])
  }, [duration])

  return (
    <ToastContext.Provider value={push}>
      {children}
      <ToastStack items={items} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

export function useToast(): Toast {
  const toast = useContext(ToastContext)
  if (!toast) throw new Error('useToast 必须在 ToastProvider 内使用')
  return toast
}
